import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, shareReplay, takeUntil } from 'rxjs/operators';
import { currentUserSubject, UserService } from '../common/services/user.service';

export const subjects = {
    PortAndCountrySubject: new BehaviorSubject<any[]>(null),
    PortAndCountryCountryOnlySubject: new BehaviorSubject<any[]>(null),
    PortSubject: new BehaviorSubject<any[]>(null)
};

@Injectable({
    providedIn: 'root'
})
export class CommonService {
    public userSubject: any = {};
    private queriesTriggered$ = new Subject<boolean>();
    public cachedCodelists: Map<string, Observable<any>> = new Map<string, Observable<any>>();
    private cachedObservableInputs: Map<string, Observable<any>> = new Map<string, Observable<any>>();

    shouldBeTakenFromCache: Map<string, boolean> = new Map<string, boolean>();

    constructor(
        private http: HttpClient,
        private userService: UserService
    ) {
        currentUserSubject
            .pipe(
                filter((user) => user != null),
                takeUntil(this.queriesTriggered$)
            )
            .subscribe((user) => {
                this.queriesTriggered$.next(true);
            });
    }

    public getDropdownOptions(entity: string, forTOSSystem?: string): Observable<any> {
        entity = this.toPascal(entity);
        // Check if the codelist is already cached and if the language is the same
        if (this.cachedCodelists.has(entity)) {
            const codelistObservable = this.cachedCodelists.get(entity);
            codelistObservable.subscribe((values: any[]) => {
                if (Array.isArray(values) && values.length > 0) {
                    const firstValue = values[0];
                    const containsCyrillic = /[а-яА-ЯЁё]/.test(firstValue?.name) || firstValue.languageId === 'BG';

                    const codelistEn = localStorage.getItem('language') === 'en' && !containsCyrillic;
                    const codelistBg = localStorage.getItem('language') === 'bg' && containsCyrillic;

                    if (codelistEn || codelistBg) {
                        this.shouldBeTakenFromCache.set(entity, true);
                    } else this.shouldBeTakenFromCache.set(entity, false);
                }
            });
        }

        // if the entity in shouldBeTakenFromCache is true, return the cached codelist
        if (this.shouldBeTakenFromCache.get(entity)) {
            return this.cachedCodelists.get(entity)!;
        }

        let entityObservable: Observable<any>;

        if (entity === 'CustomPort') {
            entityObservable = this.http.post<any>('api:///codelist/GetCodeListOptions', {
                codelistName: 'Port',
                id: '',
                filterId: '',
                filterIds: ['BGBOJ', 'BGVAR'],
                filterName: ''
            });
        } else if (entity === 'TerminalOperator') {
            entityObservable = this.http.post<any>('api:///codelist/GetTerminalOperator', {});
        } else if (entity === 'CargoAgent') {
            entityObservable = this.http.post<any>('api:///codelist/GetCargoAgents', {});
        } else if (entity === 'OrganizationShippingLine') {
            entityObservable = this.http.post<any>('api:///codelist/GetShippingLineOptions', {});
        } else if (entity === 'Port') {
            entityObservable = this.getPorts().pipe(shareReplay(1));
        } else if (entity === 'TerminalPorts') {
            entityObservable = this.http.get<any>('api:///codelist/GetTerminalPorts', {});
        } else if (entity === 'ShipmentPool') {
            entityObservable = this.http.post<any>('api:///codelist/GetShipmentPoolOptions', {});
        } else if (entity === 'OrganizationsForUserCreate') {
            entityObservable = this.http.post<any>('api:///codelist/GetOrganizationsForUserCreateOptions', {});
        } else if (entity === 'CodeListCargoTypePerSystemForManifest') {
            entityObservable = this.http.get<any>('api:///codelist/CodeListCargoTypePerSystemForManifest', {});
        } else if (entity === 'CodeListCargoPackagingTypePerSystemForManifest') {
            entityObservable = this.http.get<any>('api:///codelist/CodeListCargoPackagingTypePerSystemForManifest', {});
        } else if (entity === 'AdditionalAgentsForNomination') {
            entityObservable = this.http.get<any>('api:///codelist/GetAdditionalAgentsForNomination', {});
        } else if (entity === 'CodeListBGCustomsOffice') {
            entityObservable = this.http.get<any>('api:///codelist/CodeListBGCustomsOffice', {});
        } else if (entity === 'OrganizationsForTerminalOperator') {
            entityObservable = this.http.get<any>('api:///codelist/GetOrganizationsForTerminalOperator', {});
        } else if (entity === 'LBOOrganization') {
            entityObservable = this.http.get<any>('api:///codelist/GetLaunchBoatOperators', {});
        } else if (entity === 'CustomsWarehouse') {
            entityObservable = this.http.post<any>('api:///ServiceRequest/GetFormattedCustomsWarehouseOptions', {});
        } else if (entity === 'UnitOfMeasureOnlyVolumePerSystem') {
            entityObservable = this.http.post<any>('api:///codelist/UnitOfMeasureOnlyVolumePerSystem', {
                forTOSSystem: forTOSSystem
            });
        } else if (entity.endsWith('PerSystem')) {
            entityObservable = this.http.get<any>(`api:///codeList/${entity}/${forTOSSystem}`).pipe(
                catchError((err) => {
                    console.error(err);
                    return throwError(err);
                })
            );
        } else {
            entityObservable = this.http.post<any>('api:///codelist/GetCodeListOptions', {
                codelistName: entity,
                languageId: currentUserSubject?.value?.language?.id,
                filterId: '',
                filterName: '',
                id: ''
            });
        }

        const cachedCodelistSubject = this.addToCache(entityObservable, entity);
        return cachedCodelistSubject;
    }

    public cacheObservableInput<T>(key: string, source$: Observable<T>): Observable<T> {
        if (this.cachedObservableInputs.has(key)) {
            return this.cachedObservableInputs.get(key)!;
        }

        // use refCount + shareReplay to allow late subscribers to get last value
        const shared$ = source$.pipe(shareReplay({ bufferSize: 1, refCount: true }));
        this.cachedObservableInputs.set(key, shared$);
        return shared$;
    }

    getPorts(): Observable<any> {
        return this.http.post<any>('api:///codelist/GetCodeListOptions', {
            codelistName: 'Port',
            id: '',
            filterId: '',
            filterName: ''
        });
    }

    /** Fetches only the given items of a codelist - used by lazy-loaded dropdowns to resolve
     *  the labels of preselected values without downloading the whole codelist. */
    getCodeListItemsByIds(codeListName: string, ids: string[]): Observable<any> {
        return this.http.post<any>('api:///codelist/GetCodeListOptions', {
            codelistName: codeListName,
            id: '',
            filterId: '',
            filterName: '',
            filterIds: ids
        });
    }

    /** Server-side codelist search - returns only the top `take` items matching the term
     *  (by id, name or localized code). Used by dropdowns of large codelists (e.g. Port)
     *  so the whole codelist is never downloaded. */
    searchCodeListItems(codeListName: string, filter: string, take: number = 250): Observable<any> {
        return this.http.post<any>('api:///codelist/GetCodeListOptions', {
            codelistName: codeListName,
            id: '',
            filterId: '',
            filterName: '',
            filter: filter || '',
            take
        });
    }

    addEmailSubscription(model: any): Observable<any> {
        return this.http.post<any>('api:///common/AddEmailSubscription', model);
    }

    private addToCache(entityObservable: Observable<any>, entity: string): Observable<any> {
        const newSubject = new BehaviorSubject<any>(null);

        entityObservable.subscribe((x) => {
            newSubject.next(x);
        });

        this.cachedCodelists.set(entity, newSubject);
        return newSubject;
    }

    private toPascal(entity: string): string {
        return entity.charAt(0).toUpperCase() + entity.substring(1);
    }
}
